/**
 * Auth store — user identity and access token.
 *
 * NO persist middleware: tokens stay in memory only (security requirement).
 * Session continuity is handled via httpOnly refresh token cookie.
 */

import { create } from 'zustand'

export interface AuthUser {
  id: string
  email: string
  display_name: string
  role: string         // Legacy — kept for backward compatibility
  roles: string[]      // RBAC role names (e.g. ["admin"] or ["user", "content_creator"])
}

export interface AuthFlags {
  mustChangePassword: boolean
  mustAcceptDisclaimer: boolean
}

/** SA-906: why a view-as ended — the banner words it and settles it. */
export type ImpersonationEndReason = 'stopped' | 'expired' | 'unauthorized'

export interface Impersonation {
  /** The admin's own access token, kept aside while the target's is in use. */
  actorToken: string
  actorUser: AuthUser
  /** ISO, from the server. Never refreshed (spec D3). */
  expiresAt: string
}

export interface ImpersonationEnded {
  reason: ImpersonationEndReason
  targetUser: AuthUser
}

interface AuthState {
  /** Authenticated user profile, or null if not logged in. */
  user: AuthUser | null
  /** JWT access token (in-memory only, never persisted). */
  accessToken: string | null
  /** True once initial session restore attempt has completed. */
  isInitialized: boolean
  /** True while a login or refresh call is in progress. */
  isLoading: boolean
  /** Whether user must change their password before accessing the app. */
  mustChangePassword: boolean
  /** Whether user must accept the current disclaimer before using the app. */
  mustAcceptDisclaimer: boolean
  /** SA-906: set while the application answers for another user. In memory only (D9). */
  impersonation: Impersonation | null
  /** SA-906: an end nobody has settled yet (the banner settles it). */
  impersonationEnded: ImpersonationEnded | null

  // Derived
  readonly isAuthenticated: boolean

  // Actions
  setAuth: (user: AuthUser, token: string, flags: AuthFlags) => void
  setAccessToken: (token: string) => void
  setMustChangePassword: (value: boolean) => void
  setMustAcceptDisclaimer: (value: boolean) => void
  clearAuth: () => void
  setLoading: (loading: boolean) => void
  setInitialized: () => void
  startImpersonation: (target: AuthUser, token: string, expiresAt: string, flags: AuthFlags) => void
  stopImpersonation: (reason: ImpersonationEndReason) => ImpersonationEnded | null
  /** SA-906: a refresh that lands while viewing as is the ADMIN's (the cookie is
   * theirs): it replaces the kept token, never the one in use. No-op otherwise. */
  setActorToken: (token: string) => void
  clearImpersonationEnded: () => void
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  accessToken: null,
  isInitialized: false,
  isLoading: false,
  mustChangePassword: false,
  mustAcceptDisclaimer: false,
  impersonation: null,
  impersonationEnded: null,

  get isAuthenticated() {
    return get().user !== null && get().accessToken !== null
  },

  setAuth: (user, token, flags) =>
    set({
      user,
      accessToken: token,
      mustChangePassword: flags.mustChangePassword,
      mustAcceptDisclaimer: flags.mustAcceptDisclaimer,
      isLoading: false,
    }),

  setAccessToken: (token) => set({ accessToken: token }),

  setMustChangePassword: (value) => set({ mustChangePassword: value }),

  setMustAcceptDisclaimer: (value) => set({ mustAcceptDisclaimer: value }),

  clearAuth: () =>
    set({
      user: null,
      accessToken: null,
      mustChangePassword: false,
      mustAcceptDisclaimer: false,
      isLoading: false,
      impersonation: null,
      impersonationEnded: null,
    }),

  setLoading: (loading) => set({ isLoading: loading }),

  setInitialized: () => set({ isInitialized: true }),

  startImpersonation: (target, token, expiresAt, flags) => {
    const { user, accessToken, impersonation } = get()
    // Never nest: the kept actor token must stay the admin's (Review Focus 3).
    if (impersonation || !user || !accessToken) return
    set({
      impersonation: { actorToken: accessToken, actorUser: user, expiresAt },
      impersonationEnded: null,
      user: target,
      accessToken: token,
      mustChangePassword: flags.mustChangePassword,
      mustAcceptDisclaimer: flags.mustAcceptDisclaimer,
    })
  },

  stopImpersonation: (reason) => {
    const { impersonation, user } = get()
    if (!impersonation || !user) return null
    const ended: ImpersonationEnded = { reason, targetUser: user }
    set({
      impersonation: null,
      impersonationEnded: ended,
      user: impersonation.actorUser,
      accessToken: impersonation.actorToken,
      // The admin passed both gates to sign in (D7).
      mustChangePassword: false,
      mustAcceptDisclaimer: false,
    })
    return ended
  },

  setActorToken: (token) => {
    const { impersonation } = get()
    if (!impersonation) return
    set({ impersonation: { ...impersonation, actorToken: token } })
  },

  clearImpersonationEnded: () => set({ impersonationEnded: null }),
}))
